import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Avatar, SplitBar } from "@/components/ui/Display.jsx";
import Pagination, { PaginationInfo } from "@/components/ui/Pagination.jsx";
import { SortableTh, TableWrap, Td, Th, Tr } from "@/components/ui/Table.jsx";
import Tag from "@/components/ui/Tag.jsx";
import { nextSort, sortRows } from "@/lib/sort.js";
import { formatDuration, formatTime } from "@/lib/format.js";
import { cx } from "@/lib/cx.js";

/*
 * "Work & Idle Hours by User" — the table at the bottom of dashboard.html.
 *
 * Columns and their emphasis are the markup's, deliberately: work in emerald and
 * idle in rose is how the whole app codes those two, and Idle % is the column
 * that actually answers "is this person's day going badly" — a big work number
 * with a big idle number beside it does not.
 *
 * Rank is computed over the SORTED, UNPAGED rows, so "#1" means first by the
 * current sort rather than first on this page.
 *
 * Search, sort and pagination are client-side, deliberately: the dashboard
 * endpoint returns the whole organization in one response, so there is nothing
 * to page over on the server, and a round trip per keystroke would be slower and
 * noisier than filtering an array of at most a few hundred rows.
 */

const ACCESSORS = {
    name: (r) => r.name.toLowerCase(),
    start: (r) => (r.start ? new Date(r.start).getTime() : Infinity),
    work: (r) => r.workMs,
    idle: (r) => r.idleMs,
    idlePct: (r) => r.idlePct,
};

export default function WorkIdleTable({ rows = [], search = "" }) {
    const [sort, setSort] = useState({ by: "work", dir: "desc" });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const ranked = useMemo(() => {
        const q = search.trim().toLowerCase();
        const matched = q
            ? rows.filter(
                  (r) =>
                      r.name.toLowerCase().includes(q) ||
                      (r.team || "").toLowerCase().includes(q) ||
                      (r.email || "").toLowerCase().includes(q),
              )
            : rows;

        return sortRows(matched, sort, ACCESSORS).map((row, i) => ({ ...row, rank: i + 1 }));
    }, [rows, search, sort]);

    // The busiest person sets the bar scale, so a short day is not drawn full.
    const maxTracked = useMemo(
        () => Math.max(1, ...rows.map((r) => r.workMs + r.idleMs)),
        [rows],
    );

    const totalPages = Math.max(1, Math.ceil(ranked.length / pageSize));
    // A search that shrinks the list below the current page would otherwise show
    // an empty table with no way back except paging.
    const current = Math.min(page, totalPages);
    const visible = ranked.slice((current - 1) * pageSize, current * pageSize);

    const onSort = (field) => {
        setSort((s) => nextSort(s, field, "desc"));
        setPage(1);
    };

    return (
        <>
            <TableWrap minWidth="1040px">
                <thead>
                    <tr>
                        <Th first className="w-14">
                            #
                        </Th>
                        <SortableTh field="name" sort={sort} onSort={onSort}>
                            User
                        </SortableTh>
                        <SortableTh field="start" sort={sort} onSort={onSort} className="w-40">
                            Start time
                        </SortableTh>
                        <SortableTh field="work" sort={sort} onSort={onSort} className="w-32">
                            Work hours
                        </SortableTh>
                        <SortableTh field="idle" sort={sort} onSort={onSort} className="w-28">
                            Idle time
                        </SortableTh>
                        <SortableTh field="idlePct" sort={sort} onSort={onSort} className="w-24">
                            Idle %
                        </SortableTh>
                        <Th className="w-64">Activity (work / idle)</Th>
                        <Th last className="w-20 text-right">
                            Action
                        </Th>
                    </tr>
                </thead>

                <tbody>
                    {visible.map((row) => (
                        <Tr key={row.id}>
                            <Td first className="font-mono text-xs text-slate-400 dark:text-slate-500">
                                {row.rank}
                            </Td>

                            <Td>
                                <span className="flex min-w-0 items-center gap-2.5">
                                    <Avatar name={row.name} size="sm" />
                                    <span className="min-w-0">
                                        <Link
                                            to={`/dashboard/${row.id}`}
                                            className="block truncate font-medium hover:text-brand-600 dark:hover:text-brand-400"
                                        >
                                            {row.name}
                                        </Link>
                                        <span className="block truncate text-[11.5px] text-slate-400 dark:text-slate-500">
                                            {row.team || "No team"}
                                        </span>
                                    </span>
                                    {row.rank === 1 && <Tag tone="amber">Top</Tag>}
                                </span>
                            </Td>

                            <Td>
                                {row.started ? (
                                    <span className="flex items-center gap-1.5">
                                        <span
                                            className={cx(
                                                "font-mono",
                                                row.late && "text-amber-600 dark:text-amber-400",
                                            )}
                                        >
                                            {formatTime(row.start)}
                                        </span>
                                        {row.late && <Tag tone="amber">Late</Tag>}
                                    </span>
                                ) : (
                                    <span className="text-slate-400 dark:text-slate-500">
                                        Not started
                                    </span>
                                )}
                            </Td>

                            <Td className="font-mono text-[12.5px] font-semibold text-emerald-600 dark:text-emerald-400">
                                {formatDuration(row.workMs)}
                            </Td>

                            <Td className="font-mono text-[12.5px] font-semibold text-rose-600 dark:text-rose-400">
                                {formatDuration(row.idleMs)}
                            </Td>

                            <Td className="font-mono text-[12.5px] text-slate-500 dark:text-slate-400">
                                {row.idlePct.toFixed(1)}%
                            </Td>

                            <Td>
                                <SplitBar
                                    // Bar LENGTH is this person's tracked time against
                                    // the busiest person's, so rows compare; the SPLIT
                                    // inside it is their own work-to-idle ratio.
                                    totalPercent={((row.workMs + row.idleMs) / maxTracked) * 100}
                                    firstShare={100 - row.idlePct}
                                    title={`Work ${formatDuration(row.workMs)} · Idle ${formatDuration(row.idleMs)}`}
                                />
                            </Td>

                            <Td last className="text-right">
                                <Link
                                    to={`/dashboard/${row.id}`}
                                    className="btn-link"
                                    aria-label={`View ${row.name}`}
                                >
                                    View
                                </Link>
                            </Td>
                        </Tr>
                    ))}

                    {!visible.length && (
                        <tr>
                            <td
                                colSpan={8}
                                className="px-3 py-12 text-center text-[13px] text-slate-400 dark:text-slate-500"
                            >
                                {search
                                    ? `No user matches “${search}”`
                                    : "No activity recorded for this range."}
                            </td>
                        </tr>
                    )}
                </tbody>
            </TableWrap>

            <div className="mt-3.5 flex flex-wrap items-center gap-3">
                <PaginationInfo
                    page={current}
                    pageSize={pageSize}
                    total={ranked.length}
                    noun="users"
                    sizes={[10, 20, 50]}
                    onPageSizeChange={(n) => {
                        setPageSize(n);
                        setPage(1);
                    }}
                />
                <div className="ml-auto">
                    <Pagination page={current} totalPages={totalPages} onChange={setPage} />
                </div>
            </div>
        </>
    );
}
